// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import React from 'react'
import { bool, func, object, oneOfType, string } from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { getOptions } from './helpers'

import {
  change,
  Field,
} from 'redux-form'

import {
  castToString,
  getNestedByString,
} from 'helpers/reduxFormFunctions'

import {
  LabeledInput,
  RadioGroup,
} from 'components/Form'

import {
  convertBetweenKMandMI,
  convertBetweenMandFt,
} from 'helpers/unitConversion'

// NOTE - this will default to toggling between m <> ft (length units)
// if you need it to toggle between Km <> Mi, pass convertDistance as true
// for conversion between distance units

// NOTE - Support for Km <> Mi is simplified and allows for
// a large drift when converting back and forth. If we ever need
// a Km <> Mi toggler for values which are to be stored in the DB,
// that conversion handler will need to be more precise.

class InputWithUnitToggle extends React.Component {
  static propTypes = {
    change: func.isRequired,
    className: string,
    convertDistance: bool,
    disabled: bool,
    formatOnDisabled: bool,
    idRoot: string.isRequired,
    inputValue: string.isRequired,
    label: oneOfType([
      string,
      object,
    ]),
    nameRoot: string.isRequired,
    onBlurCustom: func,
    placeholder: string,
    selectedUnit: string.isRequired,
    sourceForm: string.isRequired,
  }

  static defaultProps = {
    className: '',
  }

  state = {
    options: [],
  }

  componentWillMount () {
    // makes it so that the options will either be
    // m <> ft
    // or
    // km <> mi
    const options = getOptions(this.props.convertDistance)
    this.setState(() => ({ options }))
  }

  // since CSS can't auto size input width based on its value length,
  // and there are times where this unit toggle should be visually closer to
  // input's value when disabled (see Antenna Details - Diameter for example)
  // For such cases, use redux-form's format method to integrate the unit label.
  // the radio group will be hidden in such a situation.
  formatInputForUI = v => {
    const {
      disabled,
      formatOnDisabled,
      selectedUnit,
    } = this.props

    // if this field is not disabled
    // OR
    // we're not supposed to format the value when it IS disabled,
    // then just return the value as is from store.
    if (!disabled || !formatOnDisabled) {
      return v
    }

    // if we're here, then that means the field is disabled
    // and we should format the value to include the unit label
    const unit = (
      v
        ? this.state.options.find(u => u.value === selectedUnit).label
        : ''
    )
    return `${v} ${unit}`
  }

  // blur handler for the input field.
  onBlur = (e, c) => {
    const {
      onBlurCustom,
      selectedUnit,
    } = this.props

    // check empty string because of redux-form bug:
    // https://github.com/erikras/redux-form/issues/3366
    // OR
    // if no custom blur was passed to this component, just do the normal redux-form blur.
    if (!e.target.value.length || !onBlurCustom) {
      return c(e)
    }

    // otherwise pass the unit toggle's value to the blur method so it can know
    // the unit of the blurred value.
    onBlurCustom(e, c, selectedUnit)
  }

  toggleInputUnit = (e, c) => {
    // allow the unit radio's change event to be passed to redux-form
    c(e)

    // convert input's value to new unit and dispatch change here.
    const {
      change,
      convertDistance,
      inputValue,
      nameRoot,
      sourceForm,
    } = this.props

    const newUnit = e.target.value
    const n = (
      convertDistance
        ? convertBetweenKMandMI(inputValue, newUnit)
        : convertBetweenMandFt(inputValue, newUnit)
    )
    const newInputValue = castToString(n)
    change(sourceForm, nameRoot, newInputValue)
  }

  // see formatInputForUI above for details on what's going on here.
  radioClasses = () => {
    const {
      disabled,
      formatOnDisabled,
    } = this.props

    const d = formatOnDisabled && disabled ? 'opacity-0 visibility-hidden' : ''

    return `flat-radios mini-flat-radios ${d}`
  }

  wrapperClasses = () => {
    const {
      className,
      disabled,
    } = this.props

    const base = 'input-with-unit-toggle position-relative'

    return `${base} ${disabled ? 'inputs-disabled' : ''} ${className}`
  }

  render () {
    const {
      formatInputForUI,
      onBlur,
      props,
      radioClasses,
      toggleInputUnit,
      wrapperClasses,
    } = this

    // NOTE: both idRoot and nameRoot here is the actual id/name of the input field
    // and for the unit toggle, are used as `${idRoot}Unit`/ `${nameRoot}Unit`
    const {
      disabled,
      idRoot,
      label,
      nameRoot,
      placeholder,
    } = props

    return (
      <div className={wrapperClasses(disabled)}>
        <Field
          component={LabeledInput}
          disabled={disabled}
          format={formatInputForUI}
          id={idRoot}
          inputClasses={'input-with-unit-toggle-input'}
          label={label}
          name={nameRoot}
          onBlurCustom={onBlur}
          placeholder={placeholder} />
        <Field
          className={radioClasses()}
          component={RadioGroup}
          disabled={disabled}
          idRoot={`${idRoot}Unit`}
          label={''}
          name={`${nameRoot}Unit`}
          onChangeCustom={toggleInputUnit}
          options={this.state.options} />
      </div>
    )
  }
}

const mapStateToProps = ({ form }, { nameRoot, sourceForm }) => {
  const { values } = form[sourceForm]
  // defaulting to empty string because of redux-form bug:
  // https://github.com/erikras/redux-form/issues/3366
  const inputValue = getNestedByString(values, nameRoot) || ''
  const selectedUnit = getNestedByString(values, `${nameRoot}Unit`)
  return {
    inputValue,
    selectedUnit,
  }
}

const mapDispatchToProps = dispatch => (
  bindActionCreators({ change }, dispatch)
)

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InputWithUnitToggle)
